import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import ExpensesInfo from '../components/ExpensesInfo'
import LoadTable from '../components/LoadTable'
import OpenMap from '../components/OpenMaps'
import StatusBadge from '../components/StatusBadge'
import { useTagContext } from '../contexts/TagContext'
import type { TagStatus } from '../types/tag'

const TagDetails: React.FC = () => {
  const { tagId } = useParams()
  const { getCurrentTag, refreshCreatedTags, isLoading } = useTagContext()
  const tag = getCurrentTag(tagId)
  const [driverSignature, setDriverSignature] = useState<string | null>(null)
  const [supervisorSignature, setSupervisorSignature] = useState<string | null>(
    null,
  )

  useEffect(() => {
    const fetchData = async () => {
      await refreshCreatedTags()
    }
    fetchData()
  }, [refreshCreatedTags])

  useEffect(() => {
    if (tag?.driver_signature) {
      setDriverSignature(tag.driver_signature)
    }
    if (tag?.supervisor_signature) {
      setSupervisorSignature(tag.supervisor_signature)
    }
  }, [tag])

  const formatTolls = (tolls: string | undefined) => {
    if (!tolls || tolls === '0') return '-'
    return `$${Number.parseFloat(tolls).toFixed(2)}`
  }

  const formatTime = (time: string | undefined) => {
    if (!time || time === '0') return '-'
    return `${time} minutes`
  }

  if (isLoading || !tag) return <div>Loading...</div>

  // Add error check for malformed tag data
  if (!tag.assigned_to) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.errorMessage)}>
          <h3>Error: Invalid Tag Data</h3>
          <p>
            This tag is missing required driver information. Please contact
            support.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <h3 className={css(styles.sectionTitle)}>TAG DETAILS</h3>
        <StatusBadge status={tag.status as TagStatus} />
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Contact Information</h4>
        <div className={css(styles.grid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Assigned Driver</span>
            <span className={css(styles.value)}>
              {tag.assigned_to.contact_object.name}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Driver Phone</span>
            <span className={css(styles.value)}>
              {tag.assigned_to.contact_object.phone_number || '-'}
            </span>
          </div>
        </div>
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Job Details</h4>
        <div className={css(styles.grid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Tag ID</span>
            <span className={css(styles.value)}>{tag.label}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Title</span>
            <span className={css(styles.value)}>{tag.title}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Origin Address</span>
            <span className={css(styles.value)}>
              {tag.origin_address ? (
                <OpenMap address={tag.origin_address} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Destination Address</span>
            <span className={css(styles.value)}>
              {tag.destination_address ? (
                <OpenMap address={tag.destination_address} />
              ) : (
                '-'
              )}
            </span>
          </div>
        </div>
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Load Information</h4>
        <LoadTable tagId={String(tag.id)} hideEditColumn />
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Additional Expenses</h4>
        <div className={css(styles.grid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Tolls</span>
            <span className={css(styles.value)}>
              {formatTolls(String(tag.additional_expenses?.tolls))}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Yard Travel Time</span>
            <span className={css(styles.value)}>
              {formatTime(String(tag.additional_expenses?.yardTime))}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Travel Time Home</span>
            <span className={css(styles.value)}>
              {formatTime(String(tag.additional_expenses?.homeTime))}
            </span>
          </div>
        </div>
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Signatures</h4>
        <div className={css(styles.signaturesContainer)}>
          <div className={css(styles.signatureSection)}>
            <h5 className={css(styles.signatureTitle)}>Driver Information</h5>
            <div className={css(styles.grid)}>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Name</span>
                <span className={css(styles.value)}>
                  {tag.assigned_to.contact_object.name}
                </span>
              </div>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Phone</span>
                <span className={css(styles.value)}>
                  {tag.assigned_to.contact_object.phone_number || '-'}
                </span>
              </div>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Email</span>
                <span className={css(styles.value)}>
                  {tag.assigned_to.contact_object.email || '-'}
                </span>
              </div>
            </div>
            <div className={css(styles.signatureBox)}>
              <p className={css(styles.confirmationText)}>
                I confirm loads have been delivered:
              </p>
              {driverSignature ? (
                <img
                  src={driverSignature}
                  alt="Driver Signature"
                  className={css(styles.signatureImage)}
                />
              ) : (
                <div className={css(styles.noSignature)}>
                  No driver signature available
                </div>
              )}
            </div>
          </div>

          <div className={css(styles.signatureSection)}>
            <h5 className={css(styles.signatureTitle)}>
              Site Supervisor Information
            </h5>
            <div className={css(styles.grid)}>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Name</span>
                <span className={css(styles.value)}>
                  {tag.site_supervisor
                    ? `${tag.site_supervisor.first_name} ${tag.site_supervisor.last_name}`.trim()
                    : '-'}
                </span>
              </div>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Phone</span>
                <span className={css(styles.value)}>
                  {tag.site_supervisor?.phone_number || '-'}
                </span>
              </div>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Email</span>
                <span className={css(styles.value)}>
                  {tag.site_supervisor?.email || '-'}
                </span>
              </div>
            </div>
            <div className={css(styles.signatureBox)}>
              <p className={css(styles.confirmationText)}>
                I confirm loads have been received:
              </p>
              {supervisorSignature ? (
                <img
                  src={supervisorSignature}
                  alt="Site Supervisor Signature"
                  className={css(styles.signatureImage)}
                />
              ) : (
                <div className={css(styles.noSignature)}>
                  No site supervisor signature available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {tag.notes && (
        <div className={css(styles.section)}>
          <h4 className={css(styles.subTitle)}>Notes</h4>
          <div className={css(styles.notes)}>{tag.notes}</div>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    padding: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    margin: 0,
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    color: '#6B7280',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    lineHeight: '1.4',
  },
  value: {
    color: '#111827',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  notes: {
    backgroundColor: '#F9FAFB',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '13px',
    lineHeight: '1.5',
    color: '#374151',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    width: '100%',
  },
  signaturesContainer: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  signatureSection: {
    flex: 1,
    minWidth: '300px',
  },
  signatureTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
  },
  signatureBox: {
    marginTop: '16px',
  },
  confirmationText: {
    fontSize: '13px',
    color: '#374151',
    marginBottom: '8px',
  },
  signatureImage: {
    width: '300px',
    height: '150px',
    objectFit: 'contain',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    padding: '8px',
    backgroundColor: '#FFFFFF',
  },
  noSignature: {
    width: '300px',
    height: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: '#6B7280',
    backgroundColor: '#F9FAFB',
    border: '1px dashed #E5E7EB',
    borderRadius: '6px',
    fontSize: '13px',
  },
  errorMessage: {
    padding: '24px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FEE2E2',
    borderRadius: '6px',
    color: '#991B1B',
    '& h3': {
      margin: '0 0 8px 0',
      fontSize: '16px',
      fontWeight: 600,
    },
    '& p': {
      margin: 0,
      fontSize: '14px',
    },
  },
})

export default TagDetails
